import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { TONE_BG, TONE_TEXT, type Tone } from '../lib/tones.js';

export type StatusTone = Extract<Tone, 'ok' | 'warn' | 'info' | 'brand' | 'dim'>;

export interface StatusDotProps {
  tone: StatusTone;
  /** Required: a dot is never the only signal (section 8.5). */
  label: ReactNode;
  /** 6px (default) or 4px inside dense grids. */
  size?: 6 | 4;
  /** Critical dots may pulse. Gated by prefers-reduced-motion in tokens.css. */
  pulse?: boolean;
  /** Mono uppercase label (default) or plain sans label. */
  mono?: boolean;
  /** Tint the label with the tone. Default: --mute for mono, --ink for sans. */
  tintLabel?: boolean;
  className?: string;
}

/**
 * A 6px circle plus a text label (DESIGN_SYSTEM section 8.5). Inside a truncating cell (a KeyValue
 * row, a DataTable cell) the label ellipsizes like plain text would; elsewhere it wraps as before.
 */
export function StatusDot({ tone, label, size = 6, pulse = false, mono = true, tintLabel = false, className }: StatusDotProps) {
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1.5', className)}>
      <span
        aria-hidden
        className={cn(
          'inline-block shrink-0 rounded-full',
          size === 6 ? 'h-1.5 w-1.5' : 'h-1 w-1',
          TONE_BG[tone],
          pulse && tone === 'brand' ? 'pulse-red' : '',
        )}
      />
      <span
        className={cn(
          'min-w-0 overflow-hidden text-ellipsis',
          mono ? 'font-mono text-2xs uppercase tracking-[0.18em]' : 'text-xs',
          tintLabel ? TONE_TEXT[tone] : mono ? 'text-mute' : 'text-ink',
        )}
      >
        {label}
      </span>
    </span>
  );
}
